// src/components/DesignQualityPanel.jsx - Visual Design Quality Analysis Display
import React, { useState } from 'react';
import { Palette, Layout, Type, Layers, Award, AlertCircle, CheckCircle, CheckCircle2, Info } from 'lucide-react';
import DesignRulesModal from './DesignRulesModal';
import { simplifyRecommendation } from '../utils/languageSimplifier';

const DesignQualityPanel = ({ designQuality, slideNumber, expertiseLevel = 'beginner' }) => {
  const [showRulesModal, setShowRulesModal] = useState(false);
  if (!designQuality || designQuality.error) {
    return null; // Don't show panel if no design data or error
  }

  const { scores, grade, violations, rules_passed } = designQuality;

  // Grade colors
  const gradeConfig = {
    'Excellent': { color: 'green', bgColor: 'bg-green-50', textColor: 'text-green-900', borderColor: 'border-green-200' },
    'Good': { color: 'blue', bgColor: 'bg-blue-50', textColor: 'text-blue-900', borderColor: 'border-blue-200' },
    'Fair': { color: 'yellow', bgColor: 'bg-yellow-50', textColor: 'text-yellow-900', borderColor: 'border-yellow-200' },
    'Needs Improvement': { color: 'red', bgColor: 'bg-red-50', textColor: 'text-red-900', borderColor: 'border-red-200' },
    'Error': { color: 'gray', bgColor: 'bg-gray-50', textColor: 'text-gray-900', borderColor: 'border-gray-200' }
  };

  const currentGrade = gradeConfig[grade] || gradeConfig['Error'];

  // Progress bar component for pillar scores
  const PillarScore = ({ icon: Icon, label, score, color }) => {
    const getColorClasses = () => {
      if (score >= 90) return { bg: 'bg-green-500', text: 'text-green-700' };
      if (score >= 75) return { bg: 'bg-blue-500', text: 'text-blue-700' };
      if (score >= 60) return { bg: 'bg-yellow-500', text: 'text-yellow-700' };
      return { bg: 'bg-red-500', text: 'text-red-700' };
    };

    const colors = getColorClasses();

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className={`w-4 h-4 ${colors.text}`} />
            <span className="text-sm font-medium text-gray-700">{label}</span>
          </div>
          <span className={`text-sm font-bold ${colors.text}`}>{Math.round(score)}/100</span>
        </div>
        <div className="bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            className={`${colors.bg} h-2 rounded-full transition-all duration-500`}
            style={{ width: `${score}%` }}
          ></div>
        </div>
      </div>
    );
  };

  // Violation item component
  const ViolationItem = ({ violation }) => {
    const severityConfig = {
      'high': { icon: AlertCircle, color: 'text-red-600', bgColor: 'bg-red-50', borderColor: 'border-red-200' },
      'medium': { icon: Info, color: 'text-yellow-600', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-200' },
      'low': { icon: CheckCircle, color: 'text-blue-600', bgColor: 'bg-blue-50', borderColor: 'border-blue-200' }
    };

    const config = severityConfig[violation.severity] || severityConfig['medium'];
    const Icon = config.icon;

    return (
      <div className={`${config.bgColor} ${config.borderColor} border rounded-lg p-3`}>
        <div className="flex items-start gap-3">
          <Icon className={`w-5 h-5 ${config.color} flex-shrink-0 mt-0.5`} />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">{simplifyRecommendation(violation.rule_name, expertiseLevel)}</p>
            <p className="text-xs text-gray-600 mt-1">{simplifyRecommendation(violation.suggestion, expertiseLevel)}</p>
          </div>
        </div>
      </div>
    );
  };

  const getSectionLabel = () => {
    return expertiseLevel === 'expert' ? 'Visual Design Quality' : 'How It Looks';
  };

  const getSubLabel = () => {
    return expertiseLevel === 'expert' ? 'Professional design principles analysis' : 'Design quality check';
  };

  const getPillarLabel = (technical) => {
    if (expertiseLevel === 'expert') return technical;
    const simplifications = {
      'Layout & Structure': 'How things are arranged',
      'Typography Hierarchy': 'Text style & size',
      'Proximity & Grouping': 'Spacing between items'
    };
    return simplifications[technical] || technical;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <Palette className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">{getSectionLabel()}</h3>
            <p className="text-xs text-muted-foreground">{getSubLabel()}</p>
          </div>
        </div>
        <div className={`${currentGrade.bgColor} ${currentGrade.borderColor} dark:bg-opacity-20 border px-3 py-1.5 rounded-lg`}>
          <div className="flex items-center gap-2">
            <Award className={`w-4 h-4 ${currentGrade.textColor}`} />
            <p className={`text-sm font-bold ${currentGrade.textColor}`}>{grade}</p>
          </div>
        </div>
      </div>

      {/* Pillar Scores - Simplified */}
      <div className="space-y-3">
        <PillarScore
          icon={Layout}
          label={getPillarLabel('Layout & Structure')}
          score={scores?.layout_structure || 0}
          color="blue"
        />
        <PillarScore
          icon={Type}
          label={getPillarLabel('Typography Hierarchy')}
          score={scores?.typography_hierarchy || 0}
          color="purple"
        />
        <PillarScore
          icon={Layers}
          label={getPillarLabel('Proximity & Grouping')}
          score={scores?.proximity_grouping || 0}
          color="green"
        />
      </div>

      {/* Design Issues or Positive Feedback */}
      {violations && violations.length > 0 ? (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {expertiseLevel === 'expert' ? `Design Issues (${violations.length})` : `Things to Fix (${violations.length})`}
          </h4>
          <div className="space-y-2">
            {violations.slice(0, 3).map((violation, index) => (
              <ViolationItem key={index} violation={violation} />
            ))}
          </div>
        </div>
      ) : scores?.overall >= 80 && (
        <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-green-700 dark:text-green-400">
                {expertiseLevel === 'expert' ? 'Strong Visual Design' : 'Looks Great!'}
              </p>
              <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                {expertiseLevel === 'expert'
                  ? 'This slide demonstrates solid design principles with no major violations.'
                  : 'Your slide follows good design practices and looks professional.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesignQualityPanel;
